import React, { useCallback } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import block from 'bem-cn'

import { ReactComponent as ProofSvg } from 'shared/img/payment/image.svg'

import { selectLocaleDict } from 'features/locale/selectors'
import { clearScreenshotsData } from 'features/payment/actions'
import { selectScreenshotsData } from 'features/payment/selectors'

import { paymentScreensConfig } from 'pages/ProfilePage/data/paymentScreens'

import { DropzoneWrapper } from 'components/DropzoneWrapper'
import { addNotify } from 'components/Notify'

import './PaymentScreens.scss'

const b = block('payment-screens')

interface PaymentScreensProps {
  enabled: boolean
  maxFiles: number
  required: boolean

  files: File[]
  onChange: (file: File[]) => void
}

export const PaymentScreens = React.memo(function PaymentScreens(props: PaymentScreensProps) {
  const { files, enabled, maxFiles, required, onChange } = props
  const { accept, maxSize, maxLengthFileName } = paymentScreensConfig.screensImg

  const dispatch = useDispatch()

  const { payment } = useSelector(selectLocaleDict, shallowEqual)
  const screenshotsData = useSelector(selectScreenshotsData, shallowEqual)

  const callbackForLargeFile = useCallback(() => {
    addNotify(
      `${payment.fileIsTooBig}. ${payment.maxSizes.replace(
        '$MAX_SIZE',
        `${(paymentScreensConfig.screensImg.maxSize / 1024 / 1024).toFixed(0)}Mb`,
      )}`,
      'error',
    )
  }, [payment])

  const callbackForUnsupportedTypeFile = useCallback(() => {
    addNotify(
      payment.fileTypeMustBe.replace('$FILES_TYPE', paymentScreensConfig.screensImg.acceptedList.join(', ')),
      'error',
    )
  }, [payment])

  const callbackLargeLengthFileName = useCallback(() => {
    addNotify(
      payment.maxFileNameLength.replace('$FILE_NAME_LENGTH', `${paymentScreensConfig.screensImg.maxLengthFileName}`),
      'error',
    )
  }, [payment])

  const handleAddFile = useCallback(
    (file: File) => {
      if (files.length >= maxFiles) return

      const newFiles = [...files, file]
      onChange(newFiles)

      dispatch(clearScreenshotsData())
    },
    [dispatch, files, maxFiles, onChange],
  )

  const handleRemove = useCallback(
    (index: number) => {
      const newFiles = files.filter((_, i) => i !== index)
      onChange(newFiles)

      dispatch(clearScreenshotsData())
    },
    [files, onChange, dispatch],
  )

  if (!enabled) return null
  return (
    <div className={b()}>
      {required && Boolean(!files.length) && <div className={b('error')}>{payment.uploadOneScreen}</div>}
      <div className={b('files-wrapper')}>
        {files?.map((file, index) => (
          <div
            key={`${file.name}_${Date.now() + index}`}
            className={b('file-wrapper', { error: Boolean(screenshotsData?.[index]?.errorMessage) })}>
            <div className={b('image-content')}>
              <img src={URL.createObjectURL(file)} alt="preview" className={b('image')} />
            </div>
            <div className={b('delete-button')} onClick={() => handleRemove(index)}>
              ✕
            </div>
          </div>
        ))}
        {files.length < maxFiles && (
          <DropzoneWrapper
            setTemporaryImage={handleAddFile}
            callbackForLargeFile={callbackForLargeFile}
            callbackLargeLengthFileName={callbackLargeLengthFileName}
            callbackForUnsupportedTypeFile={callbackForUnsupportedTypeFile}
            className={b('dropzone')}
            options={{
              accept,
              maxSize,
              maxLengthFileName,
              maxFiles,
            }}>
            <div className={b('file-wrapper', { click: true, upload: true })}>
              <span className={b('icon-wrapper')}>
                <ProofSvg className={b('icon')} />
              </span>
              <span>{payment.uploadPhoto}</span>
            </div>
          </DropzoneWrapper>
        )}
      </div>
    </div>
  )
})
